import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlusIcon, XIcon } from 'lucide-react';
import { AnnaAvatar } from '../avatar/AnnaAvatar';
import { AgentMark } from '../ui/AgentMark';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import { useDb } from '../../hooks/useDb';
import type { Tables } from '../../types/db';
type Side = 'links' | 'rechts';
interface StageCard {
  key: number;
  side: Side;
  item: Tables['summonExamples'][number];
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function SummonDemo() {
  const summonPool = useDb(t => t.summonExamples);
  const [cards, setCards] = useState<StageCard[]>([]);
  const [pulse, setPulse] = useState(0);
  const counter = useRef(0);
  const reduce = useMotionPreference();
  const summon = () => {
    const n = counter.current++;
    const next: StageCard = {
      key: n,
      side: n % 2 === 0 ? 'links' : 'rechts',
      item: summonPool[n % summonPool.length]
    };
    setCards(prev => [...prev, next].slice(-3));
    setPulse(p => p + 1);
  };
  const dismiss = (key: number) => setCards(prev => prev.filter(c => c.key !== key));
  const depthOf = (key: number) => {
    const idx = cards.findIndex(c => c.key === key);
    const fromFront = cards.length - 1 - idx;
    return {
      scale: [1, 0.94, 0.88][fromFront] ?? 0.88,
      opacity: [1, 0.8, 0.6][fromFront] ?? 0.6
    };
  };
  const column = (side: Side) => <div className={`flex flex-col gap-3 ${side === 'links' ? 'items-end' : 'items-start'}`} data-id="element-1061" data-mp="pjil8zzontrr">
      <AnimatePresence mode="popLayout" data-id="element-1062" data-mp="uhk17c1tbcz0">
        {cards.filter(c => c.side === side).map(c => {
        const from = side === 'links' ? 140 : -140;
        const depth = depthOf(c.key);
        return <motion.div key={c.key} layout={!reduce} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          scale: 0.5,
          x: from
        }} animate={{
          opacity: depth.opacity,
          scale: reduce ? 1 : depth.scale,
          x: 0
        }} exit={reduce ? {
          opacity: 0,
          transition: {
            duration: 0.2
          }
        } : {
          opacity: 0,
          scale: 0.35,
          x: from,
          transition: {
            duration: 0.25,
            ease: EASE
          }
        }} transition={reduce ? {
          duration: 0.2
        } : {
          type: 'spring',
          stiffness: 380,
          damping: 30
        }} className="glass w-full max-w-[250px] rounded-2xl p-4" data-id="element-1063" data-mp="318rspsgivoab">
                <div className="flex items-start gap-3" data-id="element-1064" data-mp="o23d3ojl79z6">
                  <AgentMark agentId={c.item.agent} size="sm" data-id="element-1065" data-mp="o0igzuhmjiz7" />
                  <div className="min-w-0 flex-1" data-id="element-1066" data-mp="j1xj41puhbdx">
                    <p className="text-[15px] font-semibold text-ink" data-id="element-1067" data-mp="j4jzmqupr18a">{c.item.title}</p>
                    <p className="mt-0.5 text-[13px] text-ink-soft" data-id="element-1068" data-mp="e5gvr9qgz11w">{c.item.line}</p>
                  </div>
                  <button type="button" onClick={() => dismiss(c.key)} aria-label={`${c.item.title} terugsturen naar Anna`} className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/10 hover:text-ink" data-id="element-1069" data-mp="2denk13emrkut">
                    <XIcon className="h-4 w-4" data-id="element-1070" data-mp="2psamuti8nglw" />
                  </button>
                </div>
              </motion.div>;
      })}
      </AnimatePresence>
    </div>;
  return <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-night-900" data-id="element-1071" data-mp="1y14q8n1q811d">
      <div aria-hidden className="absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 50%, rgb(var(--accent) / 0.09), transparent 55%)'
    }} data-id="element-1072" data-mp="2226iul151eud" />
      <div className="relative overflow-x-auto" data-id="element-1073" data-mp="2c0m78g8s6a06">
        <div className="grid min-h-[400px] min-w-[680px] grid-cols-[1fr_220px_1fr] items-center gap-4 px-6 py-8" data-id="element-1074" data-mp="2bx87zgveygwy">
          {column('links')}
          <div className="aspect-square w-full" data-id="element-1075" data-mp="3ndoprgufx9mz">
            <AnnaAvatar state="rust" pulseKey={pulse} data-id="element-1076" data-mp="3ngkoh7e442ch" />
          </div>
          {column('rechts')}
        </div>
      </div>
      <div className="relative flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] px-6 py-4" data-id="element-1077" data-mp="v7h428ijwq1r">
        <p className="text-[14px] text-ink-muted" data-id="element-1078" data-mp="1981mv4ogulv9">
          Max. 3 kaarten tegelijk. De oudste zakt naar achteren en keert als eerste terug.
        </p>
        <div className="flex gap-2" data-id="element-1079" data-mp="14a8gaaj93pug">
          <button type="button" onClick={() => setCards([])} disabled={cards.length === 0} className="h-10 whitespace-nowrap rounded-full px-4 text-[14px] font-medium text-ink-soft transition-colors duration-150 hover:text-ink disabled:opacity-40" data-id="element-1080" data-mp="1j4czjvryxdq8">
            Alles terug
          </button>
          <button type="button" onClick={summon} className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full bg-ink px-4 text-[14px] font-semibold text-night-950 transition-colors duration-150 hover:bg-white" data-id="element-1081" data-mp="1nxze5thozroo">
            <PlusIcon className="h-4 w-4" aria-hidden data-id="element-1082" data-mp="2giw73t9ruo1h" /> Roep kaart op
          </button>
        </div>
      </div>
    </div>;
}